import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import wingitDesign from '../designSystem';

export default function ClubSettingsPanel({
  clubSettingsOpen,
  onToggleClubSettings,
  airfieldDraft,
  airfieldStatus,
  airfieldError,
  onUpdateAirfieldDraft,
  onSaveClubBaseAirfield,
  appSettings,
  onUpdateSetting,
  isOwner,
  stripeConnectLoading,
  onStartStripeConnect,
  onRefreshStripeConnectStatus,
  invoiceSettings,
  invoiceSettingsStatus,
  onInvoiceSettingChange,
  onSaveInvoiceSettings,
  onResetInvoiceSettings,
  onSaveSettings,
  setupStatus,
}) {
  const ds = wingitDesign;
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [baseAirfieldExpanded, setBaseAirfieldExpanded] = useState(false);
  const [invoiceSettingsExpanded, setInvoiceSettingsExpanded] =
    useState(false);
  const [techLogDefaultsExpanded, setTechLogDefaultsExpanded] =
    useState(false);
  const [techLogDefaultsHeaderKeyboardFocus, setTechLogDefaultsHeaderKeyboardFocus] =
    useState(false);
  const [complianceExpanded, setComplianceExpanded] = useState(false);
  const [complianceHeaderKeyboardFocus, setComplianceHeaderKeyboardFocus] =
    useState(false);
const [, setWeatherLookupLoading] = useState(false);
const [weatherLookupStatus, setWeatherLookupStatus] = useState('');
const [allAirports, setAllAirports] = useState([]);
useEffect(() => {
  fetch(`${process.env.PUBLIC_URL}/airports.csv`)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Could not load airports.csv');
      }

      return response.text();
    })
    .then((text) => {
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setAllAirports(results.data);
        },
      });
    })
    .catch(() => {
      setWeatherLookupStatus(
        'Could not load the airport list.'
      );
    });
}, []);
useEffect(() => {
  const baseIcao = String(
    airfieldDraft?.icao || ''
  ).trim().toUpperCase();

  const currentWeatherIcao = String(
    airfieldDraft?.weatherIcao || ''
  ).trim().toUpperCase();

  if (baseIcao.length !== 4 || allAirports.length === 0) {
    return;
  }

  // Preserve a valid owner override.
  if (/^[A-Z]{4}$/.test(currentWeatherIcao)) {
    return;
  }

  const baseAirport = allAirports.find((airport) => {
    const airportIcao = String(
      airport.icao_code || airport.ident || ''
    ).trim().toUpperCase();

    return airportIcao === baseIcao;
  });

  if (!baseAirport) {
    setWeatherLookupStatus(
      'Base airfield was not found in the airport list.'
    );
    return;
  }

  const latitude = Number(baseAirport.latitude_deg);
  const longitude = Number(baseAirport.longitude_deg);

  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    setWeatherLookupStatus(
      'Base airfield coordinates are unavailable.'
    );
    return;
  }

  let cancelled = false;

  setWeatherLookupLoading(true);
  setWeatherLookupStatus(
    'Finding the nearest official reporting station...'
  );

  fetch(
    `/.netlify/functions/nearest-weather-station?lat=${encodeURIComponent(
      latitude
    )}&lon=${encodeURIComponent(longitude)}`
  )
    .then(async (response) => {
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(
          payload?.error ||
            'Could not find a reporting station.'
        );
      }

      return payload;
    })
    .then((payload) => {
      if (cancelled) return;

      const station = payload?.station;

      if (!station?.icao) {
        throw new Error(
          'The reporting station response was incomplete.'
        );
      }

      onUpdateAirfieldDraft(
        'weatherIcao',
        station.icao
      );

      onUpdateAirfieldDraft(
        'weatherSourceName',
        station.name || station.icao
      );

      setWeatherLookupStatus(
        `Nearest official reporting station: ${
          station.name || station.icao
        } (${station.icao}), ${station.distanceKm} km away.`
      );
    })
    .catch((error) => {
      if (!cancelled) {
        setWeatherLookupStatus(
          error.message ||
            'Could not find a reporting station.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) {
        setWeatherLookupLoading(false);
      }
    });

  return () => {
    cancelled = true;
  };
}, [
  airfieldDraft?.icao,
  airfieldDraft?.weatherIcao,
  allAirports,
  onUpdateAirfieldDraft,
]);
  const invoiceLogo =
    appSettings?.branding?.companyLogoDataUrl || '';
  const invoiceDisplayName =
    invoiceSettings?.tradingName ||
    invoiceSettings?.legalName ||
    'Your organisation';
  const stripeConnectState =
    appSettings?.payment?.stripeConnect || {};
  const stripeStatusMessage = getStripeStatusMessage(setupStatus);
  const generalSetupStatus = stripeStatusMessage ? '' : setupStatus;
  const stripeSummaryStatus = getStripeSummaryStatus({
    stripeConnectState,
    stripeStatusMessage,
  });
  const stripeNeedsAttention = stripeSummaryStatus !== 'connected';
  const stripePrimaryActionLabel = String(
    stripeConnectState?.accountId || ''
  ).trim()
    ? 'Continue setup'
    : 'Connect Stripe';
  const [stripeConnectExpanded, setStripeConnectExpanded] = useState(
    () => stripeNeedsAttention
  );

  useEffect(() => {
    if (stripeNeedsAttention) {
      setStripeConnectExpanded(true);
    }
  }, [stripeNeedsAttention]);

  const defaultTaxiTimeMinutes =
    resolveDefaultTaxiTimeMinutes(appSettings);
  const baseAirfieldSummary =
    String(airfieldDraft?.icao || '').trim() ||
    String(airfieldDraft?.name || '').trim() ||
    'Not set';
  const invoiceSummary =
    String(invoiceSettings?.tradingName || '').trim() ||
    String(invoiceSettings?.legalName || '').trim() ||
    String(invoiceSettings?.invoiceEmail || '').trim() ||
    'Not configured';
  const techLogDefaultsSummary =
    appSettings.techLog.defaultTaxiTimeEnabled !== false
      ? `Auto-fill on · ${defaultTaxiTimeMinutes} mins`
      : 'Auto-fill off';
  const complianceBaseAirfield =
    String(airfieldDraft?.icao || '').trim() || 'Not set';
  const complianceSummary = `${[
    appSettings.operational.nightCurrencyEnabled,
    appSettings.operational.checkFlightEnabled,
    appSettings.operational.bookingProhibitOnRed,
  ].filter(Boolean).length} rules enabled`;

  return (
    <section style={clubSettingsWrapStyle()}>
      <div style={clubSettingsHeaderStyle}>
        <div style={clubSettingsHeaderCopyStyle}>
          <h3 style={clubSettingsTitleStyle(ds)}>Club settings</h3>
          <p style={clubSettingsSubtitleStyle(ds)}>
            Manage base airfield, invoicing, payments and compliance from one place.
          </p>
        </div>

        <button
          type="button"
          onClick={onToggleClubSettings}
          style={ownerSecondaryButtonStyle(ds, false)}
        >
          {clubSettingsOpen ? 'Hide settings' : 'Edit settings'}
        </button>
      </div>

      {clubSettingsOpen ? (
        <div style={sectionsStackStyle}>
          <section style={uniformSectionCardStyle(ds)}>
            <button
              type="button"
              onClick={() =>
                setBaseAirfieldExpanded((previous) => !previous)
              }
              aria-expanded={baseAirfieldExpanded}
              style={collapseHeaderButtonStyle(ds)}
            >
              <span style={collapseHeaderTitleStyle(ds)}>
                Club Base Airfield
              </span>
              <span style={collapseHeaderSummaryStyle}>
                <span style={collapseSummaryTextStyle(ds)}>
                  {baseAirfieldSummary}
                </span>
                <span
                  aria-hidden="true"
                  style={chevronStyle(ds, baseAirfieldExpanded)}
                >
                  ▾
                </span>
              </span>
            </button>

            {baseAirfieldExpanded ? (
              <>
                <div style={uniformSectionHeaderStyle}>
                  <p style={uniformSectionDescriptionStyle(ds)}>
                    Set the club base and the nearby weather source used throughout Wingit.
                  </p>
                </div>

                <div style={uniformSectionBodyStyle}>
                  <form onSubmit={onSaveClubBaseAirfield}>
                    <div style={compactFieldGridStyle}>
                      <label style={formLabelStyle}>
                        Airfield name
                        <input
                          type="text"
                          value={airfieldDraft.name}
                          onChange={(event) =>
                            onUpdateAirfieldDraft(
                              'name',
                              event.target.value
                            )
                          }
                          placeholder="North Weald"
                          style={formInputStyle}
                        />
                      </label>

                      <label style={formLabelStyle}>
                        Airfield ICAO
                        <input
                          type="text"
                          value={airfieldDraft.icao}
                          onChange={(event) =>
                            onUpdateAirfieldDraft(
                              'icao',
                              event.target.value
                            )
                          }
                          placeholder="EGSX"
                          style={formInputStyle}
                        />
                      </label>


                      <label style={formLabelStyle}>
                        Weather reporting station (ICAO)
                        <input
                          type="text"
                          value={airfieldDraft.weatherIcao}
                          onChange={(event) =>
                            onUpdateAirfieldDraft(
                              'weatherIcao',
                              event.target.value
                            )
                          }
                          placeholder="EGSS"
                          style={formInputStyle}
                        />
                        {weatherLookupStatus ? (
  <span
    style={{
      display: 'block',
      marginTop: 6,
      fontSize: 12,
      fontWeight: 400,
      opacity: 0.8,
    }}
  >
    {weatherLookupStatus}
  </span>
) : null}
                      </label>
                    </div>

                    <label style={{ ...formLabelStyle, marginTop: 12 }}>
                      Notes
                      <textarea
                        value={airfieldDraft.notes}
                        onChange={(event) =>
                          onUpdateAirfieldDraft(
                            'notes',
                            event.target.value
                          )
                        }
                        placeholder="Example: North Weald does not normally issue METAR/TAF. Stansted is used as the nearby aviation weather source."
                        rows={3}
                        style={{
                          ...formInputStyle,
                          resize: 'vertical',
                        }}
                      />
                    </label>

                    <div style={ownerActionRowStyle}>
                      <button
                        type="submit"
                        style={ownerPrimaryButtonStyle(ds, false)}
                      >
                        Save Club Base Airfield
                      </button>
                    </div>
                  </form>

                  {airfieldStatus ? (
                    <div style={feedbackMessageStyle(ds, 'info')}>
                      {airfieldStatus}
                    </div>
                  ) : null}

                  {airfieldError ? (
                    <div style={feedbackMessageStyle(ds, 'danger')}>
                      {airfieldError}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </section>

          <section style={uniformSectionCardStyle(ds)}>
            <button
              type="button"
              onClick={() =>
                setInvoiceSettingsExpanded((previous) => !previous)
              }
              aria-expanded={invoiceSettingsExpanded}
              style={collapseHeaderButtonStyle(ds)}
            >
              <span style={collapseHeaderTitleStyle(ds)}>
                Invoice Settings
              </span>
              <span style={collapseHeaderSummaryStyle}>
                <span style={collapseSummaryTextStyle(ds)}>
                  {invoiceSummary}
                </span>
                <span
                  aria-hidden="true"
                  style={chevronStyle(ds, invoiceSettingsExpanded)}
                >
                  ▾
                </span>
              </span>
            </button>

            {invoiceSettingsExpanded ? (
              <>
                <div style={uniformSectionHeaderStyle}>
                  <p style={uniformSectionDescriptionStyle(ds)}>
                    Configure invoice details and preview output without changing invoice generation.
                  </p>
                </div>

                <div style={uniformSectionBodyStyle}>
                  <div style={invoiceFieldsGridStyle}>
                    <label style={formLabelStyle}>
                      Legal organisation/company name
                      <input
                        type="text"
                        value={invoiceSettings?.legalName || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'legalName',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Trading name
                      <input
                        type="text"
                        value={invoiceSettings?.tradingName || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'tradingName',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Invoice email
                      <input
                        type="email"
                        value={invoiceSettings?.invoiceEmail || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'invoiceEmail',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Telephone
                      <input
                        type="text"
                        value={invoiceSettings?.telephone || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'telephone',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Company registration number
                      <input
                        type="text"
                        value={
                          invoiceSettings?.companyRegistrationNumber || ''
                        }
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'companyRegistrationNumber',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      VAT registration number
                      <input
                        type="text"
                        value={
                          invoiceSettings?.vatRegistrationNumber || ''
                        }
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'vatRegistrationNumber',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Invoice prefix
                      <input
                        type="text"
                        value={invoiceSettings?.invoicePrefix || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'invoicePrefix',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Next invoice number
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={invoiceSettings?.nextInvoiceNumber || 1}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'nextInvoiceNumber',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Payment terms in days
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={invoiceSettings?.paymentTermsDays || 0}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'paymentTermsDays',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Default VAT rate (%)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={invoiceSettings?.defaultVatRate || 0}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'defaultVatRate',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Bank account name
                      <input
                        type="text"
                        value={invoiceSettings?.bankAccountName || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'bankAccountName',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Bank name
                      <input
                        type="text"
                        value={invoiceSettings?.bankName || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'bankName',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Sort code
                      <input
                        type="text"
                        value={invoiceSettings?.sortCode || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'sortCode',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                    <label style={formLabelStyle}>
                      Account number
                      <input
                        type="text"
                        value={invoiceSettings?.accountNumber || ''}
                        onChange={(event) =>
                          onInvoiceSettingChange(
                            'accountNumber',
                            event.target.value
                          )
                        }
                        style={formInputStyle}
                      />
                    </label>
                  </div>

                  <label style={{ ...formLabelStyle, marginTop: 10 }}>
                    Invoice address
                    <textarea
                      rows={3}
                      value={invoiceSettings?.invoiceAddress || ''}
                      onChange={(event) =>
                        onInvoiceSettingChange(
                          'invoiceAddress',
                          event.target.value
                        )
                      }
                      style={{ ...formInputStyle, resize: 'vertical' }}
                    />
                  </label>

                  <label style={{ ...formLabelStyle, marginTop: 10 }}>
                    Payment reference instructions
                    <textarea
                      rows={2}
                      value={
                        invoiceSettings?.paymentReferenceInstructions || ''
                      }
                      onChange={(event) =>
                        onInvoiceSettingChange(
                          'paymentReferenceInstructions',
                          event.target.value
                        )
                      }
                      style={{ ...formInputStyle, resize: 'vertical' }}
                    />
                  </label>

                  <label style={{ ...formLabelStyle, marginTop: 10 }}>
                    Invoice footer note
                    <textarea
                      rows={2}
                      value={invoiceSettings?.invoiceFooterNote || ''}
                      onChange={(event) =>
                        onInvoiceSettingChange(
                          'invoiceFooterNote',
                          event.target.value
                        )
                      }
                      style={{ ...formInputStyle, resize: 'vertical' }}
                    />
                  </label>

                  <div style={ownerActionRowStyle}>
                    <button
                      type="button"
                      onClick={onSaveInvoiceSettings}
                      style={ownerPrimaryButtonStyle(ds, false)}
                    >
                      Save Invoice Settings
                    </button>
                    <button
                      type="button"
                      onClick={onResetInvoiceSettings}
                      style={ownerSecondaryButtonStyle(ds, false)}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setShowInvoicePreview((previous) => !previous)
                      }
                      style={ownerSecondaryButtonStyle(ds, false)}
                    >
                      {showInvoicePreview
                        ? 'Hide Preview'
                        : 'Preview Invoice'}
                    </button>
                  </div>

                  {invoiceSettingsStatus ? (
                    <div style={feedbackMessageStyle(ds, 'success')}>
                      {invoiceSettingsStatus}
                    </div>
                  ) : null}

                  {showInvoicePreview ? (
                    <div style={invoicePreviewCardStyle}>
                      <div style={invoicePreviewHeaderStyle}>
                        <div>
                          {invoiceLogo ? (
                            <img
                              src={invoiceLogo}
                              alt="Organisation logo"
                              style={invoicePreviewLogoStyle}
                            />
                          ) : null}
                          <div style={invoicePreviewTitleStyle}>
                            {invoiceDisplayName}
                          </div>
                          <div style={invoicePreviewMetaStyle}>
                            {invoiceSettings?.legalName || ''}
                          </div>
                          <div style={invoicePreviewMetaStyle}>
                            {invoiceSettings?.invoiceAddress || ''}
                          </div>
                        </div>

                        <div style={invoicePreviewMetaStyle}>
                          Invoice {invoiceSettings?.invoicePrefix || 'INV'}-
                          {String(
                            invoiceSettings?.nextInvoiceNumber || 1
                          ).padStart(4, '0')}
                        </div>
                      </div>

                      <div style={invoicePreviewLineStyle}>
                        <span>Aircraft</span>
                        <strong>G-ABCD (PA-28)</strong>
                      </div>
                      <div style={invoicePreviewLineStyle}>
                        <span>Flight date</span>
                        <strong>19 July 2026</strong>
                      </div>
                      <div style={invoicePreviewLineStyle}>
                        <span>Chargeable time</span>
                        <strong>1.5 hours</strong>
                      </div>
                      <div style={invoicePreviewLineStyle}>
                        <span>Aircraft charge</span>
                        <strong>£225.00</strong>
                      </div>
                      <div style={invoicePreviewTotalStyle}>
                        <span>Total</span>
                        <strong>£225.00</strong>
                      </div>
                      <div style={invoicePreviewMetaStyle}>
                        Payment terms: {invoiceSettings?.paymentTermsDays || 0} days
                      </div>

                      {invoiceSettings?.invoiceFooterNote ? (
                        <div style={invoicePreviewFootnoteStyle}>
                          {invoiceSettings.invoiceFooterNote}
                        </div>
                      ) : null}

                      <div style={invoicePreviewWingitStyle}>
                        Flight administration powered by Wingit
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </section>

          <section style={uniformSectionCardStyle(ds)}>
            <button
              type="button"
              onClick={() =>
                setTechLogDefaultsExpanded((previous) => !previous)
              }
              onFocus={(event) => {
                setTechLogDefaultsHeaderKeyboardFocus(
                  event.currentTarget.matches(':focus-visible')
                );
              }}
              onBlur={() => setTechLogDefaultsHeaderKeyboardFocus(false)}
              aria-expanded={techLogDefaultsExpanded}
              style={techLogDefaultsCollapseHeaderButtonStyle(
                ds,
                techLogDefaultsHeaderKeyboardFocus
              )}
            >
              <span style={collapseHeaderTitleStyle(ds)}>
                Tech Log Defaults
              </span>
              <span style={collapseHeaderSummaryStyle}>
                <span style={collapseSummaryTextStyle(ds)}>
                  {techLogDefaultsSummary}
                </span>
                <span
                  aria-hidden="true"
                  style={chevronStyle(ds, techLogDefaultsExpanded)}
                >
                  ▾
                </span>
              </span>
            </button>

            {techLogDefaultsExpanded ? (
              <>
                <div style={uniformSectionHeaderStyle}>
                  <p style={uniformSectionDescriptionStyle(ds)}>
                    Set the default taxi time applied in tech log workflows.
                  </p>
                </div>

                <div style={techLogDefaultsBodyStyle}>
                  <label style={techLogDefaultsCheckboxRowStyle(ds)}>
                    <input
                      type="checkbox"
                      checked={
                        appSettings.techLog.defaultTaxiTimeEnabled !== false
                      }
                      onChange={(event) =>
                        onUpdateSetting(
                          ['techLog', 'defaultTaxiTimeEnabled'],
                          event.target.checked
                        )
                      }
                      style={techLogDefaultsCheckboxInputStyle}
                    />
                    <span style={techLogDefaultsCheckboxTextStyle(ds)}>
                      Auto-fill Taxi Time from owner default
                    </span>
                  </label>

                  {appSettings.techLog.defaultTaxiTimeEnabled !== false ? (
                    <div style={techLogDefaultsValueRowStyle}>
                      <label style={techLogDefaultsMinutesLabelStyle(ds)}>
                        Default taxi time (mins)
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={defaultTaxiTimeMinutes}
                          onChange={(event) =>
                            onUpdateSetting(
                              ['techLog', 'defaultTaxiTimeMinutes'],
                              event.target.value
                            )
                          }
                          style={techLogDefaultsMinutesInputStyle}
                        />
                      </label>
                      <div style={techLogDefaultsDecimalTextStyle(ds)}>
                        Stored as decimal hours: {Number((defaultTaxiTimeMinutes / 60).toFixed(2))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </section>

          <section style={uniformSectionCardStyle(ds)}>
            <button
              type="button"
              onClick={() =>
                setStripeConnectExpanded((previous) => !previous)
              }
              aria-expanded={stripeConnectExpanded}
              style={collapseHeaderButtonStyle(ds)}
            >
              <span style={collapseHeaderTitleStyle(ds)}>Payment</span>
              <span style={collapseHeaderSummaryStyle}>
                <span
                  style={statusBadgeStyle(
                    ds,
                    stripeSummaryStatus === 'connected'
                      ? 'success'
                      : stripeSummaryStatus === 'setup_required'
                        ? 'warning'
                        : 'danger'
                  )}
                >
                  {getStripeSummaryLabel(stripeSummaryStatus)}
                </span>
                <span
                  aria-hidden="true"
                  style={chevronStyle(ds, stripeConnectExpanded)}
                >
                  ▾
                </span>
              </span>
            </button>

            {stripeConnectExpanded ? (
              <div style={collapsibleContentStyle}>
                <p style={uniformSectionDescriptionStyle(ds)}>
                  Configure billing defaults and monitor Stripe onboarding without changing the connection logic.
                </p>

                <div style={paymentControlsGridStyle}>
                  <label style={compactFieldLabelStyle(ds)}>
                    Hourly rate (£)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={appSettings.payment.hourlyRate}
                      onChange={(event) =>
                        onUpdateSetting(
                          ['payment', 'hourlyRate'],
                          event.target.value
                        )
                      }
                      style={{ ...formInputStyle, maxWidth: 140 }}
                    />
                  </label>

                  <div style={paymentLandingFeeColumnStyle}>
                    <label style={checkboxRowStyle(ds)}>
                      <input
                        type="checkbox"
                        checked={appSettings.payment.landingFeeEnabled}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['payment', 'landingFeeEnabled'],
                            event.target.checked
                          )
                        }
                        style={checkboxInputStyle}
                      />
                      <span>
                        <span style={checkboxTitleStyle(ds)}>
                          Include landing fee
                        </span>
                      </span>
                    </label>

                    {appSettings.payment.landingFeeEnabled ? (
                      <label style={compactFieldLabelStyle(ds)}>
                        Landing fee (£)
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={appSettings.payment.landingFee}
                          onChange={(event) =>
                            onUpdateSetting(
                              ['payment', 'landingFee'],
                              event.target.value
                            )
                          }
                          style={{ ...formInputStyle, maxWidth: 140 }}
                        />
                      </label>
                    ) : null}
                  </div>
                </div>

                <div style={stripeTopRowStyle}>
                  <label style={compactFieldLabelStyle(ds)}>
                    Connect mode
                    <select
                      value={stripeConnectState?.mode || 'standard'}
                      onChange={(event) =>
                        onUpdateSetting(
                          ['payment', 'stripeConnect'],
                          {
                            ...stripeConnectState,
                            mode:
                              event.target.value === 'express'
                                ? 'express'
                                : 'standard',
                          }
                        )
                      }
                      style={{ ...formInputStyle, maxWidth: 260 }}
                      disabled={stripeConnectLoading || !isOwner}
                    >
                      <option value="standard">
                        Standard (recommended)
                      </option>
                      <option value="express">Express</option>
                    </select>
                  </label>

                  <div style={statusBadgeRowStyle}>
                    <span
                      style={statusBadgeStyle(
                        ds,
                        stripeConnectState?.accountId ? 'info' : 'muted'
                      )}
                    >
                      {stripeConnectState?.accountId
                        ? 'Account linked'
                        : 'Not linked'}
                    </span>
                    <span
                      style={statusBadgeStyle(
                        ds,
                        stripeConnectState?.chargesEnabled &&
                          stripeConnectState?.payoutsEnabled
                          ? 'success'
                          : 'warning'
                      )}
                    >
                      {stripeConnectState?.chargesEnabled &&
                      stripeConnectState?.payoutsEnabled
                        ? 'Ready for payments'
                        : 'Setup required'}
                    </span>
                  </div>
                </div>

                <div style={stripeStatusGridStyle}>
                  <div style={statusCardStyle(ds)}>
                    <div style={statusLabelStyle}>Account</div>
                    <div style={statusValueStyle(ds)}>
                      {stripeConnectState?.accountId || 'Not linked'}
                    </div>
                  </div>
                  <div style={statusCardStyle(ds)}>
                    <div style={statusLabelStyle}>Status</div>
                    <div style={statusValueStyle(ds)}>
                      {stripeConnectState?.status || 'Unlinked'}
                    </div>
                  </div>
                  <div style={statusCardStyle(ds)}>
                    <div style={statusLabelStyle}>Payments</div>
                    <div
                      style={statusValueStyle(
                        ds,
                        stripeConnectState?.chargesEnabled
                          ? 'success'
                          : 'warning'
                      )}
                    >
                      {stripeConnectState?.chargesEnabled
                        ? 'Ready'
                        : 'Setup required'}
                    </div>
                  </div>
                  <div style={statusCardStyle(ds)}>
                    <div style={statusLabelStyle}>Payouts</div>
                    <div
                      style={statusValueStyle(
                        ds,
                        stripeConnectState?.payoutsEnabled
                          ? 'success'
                          : 'warning'
                      )}
                    >
                      {stripeConnectState?.payoutsEnabled
                        ? 'Ready'
                        : 'Setup required'}
                    </div>
                  </div>
                </div>

                {stripeStatusMessage ? (
                  <div
                    style={feedbackMessageStyle(
                      ds,
                      getMessageTone(stripeStatusMessage)
                    )}
                  >
                    {stripeStatusMessage}
                  </div>
                ) : null}

                {!stripeConnectState?.chargesEnabled ||
                !stripeConnectState?.payoutsEnabled ? (
                  <div style={feedbackMessageStyle(ds, 'warning')}>
                    Payments stay unavailable until both charges and payouts are enabled.
                  </div>
                ) : null}

                <div style={ownerActionRowStyle}>
                  <button
                    type="button"
                    onClick={onStartStripeConnect}
                    disabled={stripeConnectLoading || !isOwner}
                    style={ownerPrimaryButtonStyle(
                      ds,
                      stripeConnectLoading || !isOwner
                    )}
                  >
                    {stripeConnectLoading
                      ? 'Working...'
                      : stripePrimaryActionLabel}
                  </button>
                  <button
                    type="button"
                    onClick={onRefreshStripeConnectStatus}
                    disabled={stripeConnectLoading || !isOwner}
                    style={ownerSecondaryButtonStyle(
                      ds,
                      stripeConnectLoading || !isOwner
                    )}
                  >
                    Refresh status
                  </button>
                </div>

                {!isOwner ? (
                  <div style={ownerNoteStyle(ds)}>
                    Only the owner can manage Stripe Connect.
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section style={uniformSectionCardStyle(ds)}>
            <button
              type="button"
              onClick={() =>
                setComplianceExpanded((previous) => !previous)
              }
              onFocus={(event) => {
                setComplianceHeaderKeyboardFocus(
                  event.currentTarget.matches(':focus-visible')
                );
              }}
              onBlur={() => setComplianceHeaderKeyboardFocus(false)}
              aria-expanded={complianceExpanded}
              style={complianceCollapseHeaderButtonStyle(
                ds,
                complianceHeaderKeyboardFocus
              )}
            >
              <span style={collapseHeaderTitleStyle(ds)}>Compliance</span>
              <span style={collapseHeaderSummaryStyle}>
                <span style={collapseSummaryTextStyle(ds)}>
                  {complianceSummary}
                </span>
                <span
                  aria-hidden="true"
                  style={chevronStyle(ds, complianceExpanded)}
                >
                  ▾
                </span>
              </span>
            </button>

            {complianceExpanded ? (
              <>
                <div style={ownerSectionHeaderStyle}>
                  <p style={uniformSectionDescriptionStyle(ds)}>
                    Configure warning thresholds and club-level compliance rules.
                  </p>
                </div>

                <div style={complianceGroupStyle}>
                  <div style={ownerGroupTitleStyle(ds)}>
                    Airfield and warning thresholds
                  </div>
                  <div style={compactFieldGridStyle}>
                    <label style={compactFieldLabelStyle(ds)}>
                      Base airfield
                      <input
                        type="text"
                        value={complianceBaseAirfield}
                        style={{
                          ...formInputStyle,
                          maxWidth: 160,
                          cursor: 'default',
                          opacity: 0.9,
                        }}
                        readOnly
                        disabled
                      />
                    </label>
                    <label style={compactFieldLabelStyle(ds)}>
                      Medical warning
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={appSettings.operational.medicalWarningDays}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'medicalWarningDays'],
                            event.target.value
                          )
                        }
                        style={{ ...formInputStyle, maxWidth: 120 }}
                      />
                    </label>
                    <label style={compactFieldLabelStyle(ds)}>
                      Currency warning
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={appSettings.operational.currencyWarningDays}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'currencyWarningDays'],
                            event.target.value
                          )
                        }
                        style={{ ...formInputStyle, maxWidth: 120 }}
                      />
                    </label>
                    <label style={compactFieldLabelStyle(ds)}>
                      Check flight warning
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={appSettings.operational.checkFlightWarningDays}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'checkFlightWarningDays'],
                            event.target.value
                          )
                        }
                        style={{ ...formInputStyle, maxWidth: 120 }}
                      />
                    </label>
                    <label style={compactFieldLabelStyle(ds)}>
                      Currency window
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={appSettings.operational.clubCurrencyWindowDays}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'clubCurrencyWindowDays'],
                            event.target.value
                          )
                        }
                        style={{ ...formInputStyle, maxWidth: 120 }}
                      />
                    </label>
                  </div>

                  {!isOwner ? (
                    <div style={ownerNoteStyle(ds)}>
                      Only the owner can change the base airfield.
                    </div>
                  ) : null}
                </div>

                <div style={complianceGroupStyle}>
                  <div style={ownerGroupTitleStyle(ds)}>
                    Compliance rules
                  </div>
                  <div style={checkboxColumnStyle}>
                    <label style={complianceCheckboxRowStyle(ds)}>
                      <input
                        type="checkbox"
                        checked={appSettings.operational.nightCurrencyEnabled}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'nightCurrencyEnabled'],
                            event.target.checked
                          )
                        }
                        style={complianceCheckboxInputStyle}
                      />
                      <span style={complianceCheckboxTextStyle(ds)}>
                        Enable night currency tracking
                      </span>
                    </label>
                    <label style={complianceCheckboxRowStyle(ds)}>
                      <input
                        type="checkbox"
                        checked={appSettings.operational.checkFlightEnabled}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'checkFlightEnabled'],
                            event.target.checked
                          )
                        }
                        style={complianceCheckboxInputStyle}
                      />
                      <span style={complianceCheckboxTextStyle(ds)}>
                        Enable check flight due tracking
                      </span>
                    </label>
                    <label style={complianceCheckboxRowStyle(ds)}>
                      <input
                        type="checkbox"
                        checked={appSettings.operational.bookingProhibitOnRed}
                        onChange={(event) =>
                          onUpdateSetting(
                            ['operational', 'bookingProhibitOnRed'],
                            event.target.checked
                          )
                        }
                        style={complianceCheckboxInputStyle}
                      />
                      <span style={complianceCheckboxTextStyle(ds)}>
                        Prohibit booking when compliance status is red
                      </span>
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onSaveSettings}
                  style={{
                    ...ownerPrimaryButtonStyle(ds, false),
                    marginTop: 12,
                  }}
                >
                  Save settings
                </button>

                {generalSetupStatus ? (
                  <div
                    style={feedbackMessageStyle(
                      ds,
                      getMessageTone(generalSetupStatus)
                    )}
                  >
                    {generalSetupStatus}
                  </div>
                ) : null}
              </>
            ) : null}
          </section>
        </div>
      ) : null}
    </section>
  );
}

const formLabelStyle = {
  display: 'block',
  color: '#c9d9ec',
  fontSize: '0.82rem',
  fontWeight: 650,
  lineHeight: 1.35,
};

const formInputStyle = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  minHeight: 36,
  padding: '0.5rem 0.7rem',
  borderRadius: 10,
  border: '1px solid rgba(120, 180, 255, 0.22)',
  background: 'rgba(12, 22, 36, 0.82)',
  color: '#f3f8ff',
  fontSize: '0.86rem',
  boxSizing: 'border-box',
};

function clubSettingsWrapStyle() {
  return {
    marginTop: '1.5rem',
  };
}

const clubSettingsHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: '0.85rem',
  flexWrap: 'wrap',
};

const clubSettingsHeaderCopyStyle = {
  display: 'grid',
  gap: 4,
};

function clubSettingsTitleStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '1.04rem',
    fontWeight: 700,
    lineHeight: 1.2,
    margin: 0,
  };
}

function clubSettingsSubtitleStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.82rem',
    lineHeight: 1.45,
    margin: 0,
    maxWidth: 620,
  };
}

const sectionsStackStyle = {
  display: 'grid',
  gap: 12,
};

function uniformSectionCardStyle(ds) {
  return {
    ...ds.cards.section,
    marginBottom: 0,
    display: 'grid',
    gap: 12,
  };
}

const uniformSectionHeaderStyle = {
  display: 'grid',
  gap: 4,
};

function uniformSectionDescriptionStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.82rem',
    lineHeight: 1.45,
    margin: 0,
    maxWidth: 620,
  };
}

const uniformSectionBodyStyle = {
  display: 'grid',
  gap: 12,
};

const invoiceFieldsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

function collapseHeaderButtonStyle(ds) {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '0.1rem 0',
    background: 'transparent',
    border: 'none',
    borderRadius: ds.radius.medium,
    color: ds.colors.text,
    textAlign: 'left',
    cursor: 'pointer',
    outlineOffset: 4,
  };
}

function techLogDefaultsCollapseHeaderButtonStyle(ds, keyboardFocused) {
  return {
    ...collapseHeaderButtonStyle(ds),
    border: `1px solid ${ds.colors.border}`,
    padding: '0.65rem 0.8rem',
    transition: ds.transition.fast,
    outline: keyboardFocused ? '2px solid rgba(85, 199, 255, 0.28)' : 'none',
    outlineOffset: 2,
  };
}

function complianceCollapseHeaderButtonStyle(ds, keyboardFocused) {
  return {
    ...collapseHeaderButtonStyle(ds),
    border: `1px solid ${ds.colors.border}`,
    padding: '0.65rem 0.8rem',
    transition: ds.transition.fast,
    outline: keyboardFocused ? '2px solid rgba(85, 199, 255, 0.28)' : 'none',
    outlineOffset: 2,
  };
}

function collapseHeaderTitleStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.2,
  };
}

const collapseHeaderSummaryStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
};

function collapseSummaryTextStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.8rem',
    lineHeight: 1.35,
    textAlign: 'right',
  };
}

function chevronStyle(ds, expanded) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.9rem',
    lineHeight: 1,
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: ds.transition.fast,
  };
}

const collapsibleContentStyle = {
  display: 'grid',
  gap: 12,
  marginTop: 10,
};

const paymentControlsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  alignItems: 'start',
};

const paymentLandingFeeColumnStyle = {
  display: 'grid',
  gap: 10,
  alignContent: 'start',
};

function compactFieldLabelStyle(ds) {
  return {
    color: ds.colors.textSoft,
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1.35,
    display: 'grid',
    gap: 0,
  };
}

const ownerActionRowStyle = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  alignItems: 'center',
  marginTop: 12,
};

function ownerPrimaryButtonStyle(ds, disabled) {
  return {
    ...ds.buttons.primary,
    minHeight: 36,
    padding: '0.5rem 0.85rem',
    borderRadius: ds.radius.medium,
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : 1,
    boxSizing: 'border-box',
  };
}

function ownerSecondaryButtonStyle(ds, disabled) {
  return {
    ...ds.buttons.secondary,
    minHeight: 36,
    padding: '0.5rem 0.85rem',
    borderRadius: ds.radius.medium,
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : 1,
    boxSizing: 'border-box',
  };
}

function feedbackMessageStyle(ds, tone) {
  const chip =
    tone === 'danger'
      ? ds.chips.danger
      : tone === 'warning'
        ? ds.chips.warning
        : tone === 'success'
          ? ds.chips.success
          : ds.chips.info;

  return {
    ...chip,
    borderRadius: ds.radius.medium,
    padding: '0.65rem 0.75rem',
    fontSize: '0.82rem',
    lineHeight: 1.4,
  };
}

const stripeTopRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
  alignItems: 'end',
};

const statusBadgeRowStyle = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  alignItems: 'center',
};

function statusBadgeStyle(ds, tone) {
  const chip =
    tone === 'success'
      ? ds.chips.success
      : tone === 'warning'
        ? ds.chips.warning
        : tone === 'muted'
          ? ds.chips.muted
          : ds.chips.info;

  return {
    ...chip,
    borderRadius: 999,
    padding: '0.28rem 0.55rem',
    fontSize: '0.75rem',
    fontWeight: 650,
    lineHeight: 1.2,
  };
}

const stripeStatusGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 10,
};

const statusLabelStyle = {
  color: '#9fb7d4',
  fontSize: '0.76rem',
  fontWeight: 650,
  marginBottom: 4,
};

function statusCardStyle(ds) {
  return {
    background: 'rgba(12, 22, 36, 0.72)',
    border: `1px solid ${ds.colors.border}`,
    borderRadius: ds.radius.medium,
    padding: '0.75rem',
    minWidth: 0,
  };
}

function statusValueStyle(ds, tone) {
  const color =
    tone === 'success'
      ? '#b9ffd4'
      : tone === 'warning'
        ? '#ffe3a3'
        : ds.colors.text;

  return {
    color,
    fontSize: '0.86rem',
    fontWeight: 650,
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
  };
}

const ownerSectionHeaderStyle = {
  display: 'grid',
  gap: 4,
};

function ownerNoteStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.8rem',
    lineHeight: 1.4,
  };
}

const compactFieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 12,
  alignItems: 'start',
};

const checkboxColumnStyle = {
  display: 'grid',
  gap: 8,
};

function checkboxRowStyle(ds) {
  return {
    display: 'grid',
    gridTemplateColumns: '18px minmax(0, 1fr)',
    gap: 10,
    alignItems: 'start',
    color: ds.colors.text,
  };
}

const checkboxInputStyle = {
  margin: '2px 0 0',
};

function techLogDefaultsCheckboxRowStyle(ds) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    width: 'fit-content',
    maxWidth: '100%',
    color: ds.colors.text,
    cursor: 'pointer',
  };
}

const techLogDefaultsCheckboxInputStyle = {
  margin: 0,
  flexShrink: 0,
};

function techLogDefaultsCheckboxTextStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '0.84rem',
    fontWeight: 650,
    lineHeight: 1.4,
  };
}

const techLogDefaultsBodyStyle = {
  display: 'grid',
  gap: 10,
};

const techLogDefaultsValueRowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  gap: 10,
  rowGap: 8,
  width: '100%',
};

function techLogDefaultsMinutesLabelStyle(ds) {
  return {
    color: ds.colors.textSoft,
    fontSize: '0.82rem',
    fontWeight: 650,
    lineHeight: 1.35,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  };
}

const techLogDefaultsMinutesInputStyle = {
  ...formInputStyle,
  maxWidth: 120,
  marginTop: 0,
  flex: '0 0 120px',
};

function techLogDefaultsDecimalTextStyle(ds) {
  return {
    color: ds.colors.textMuted,
    fontSize: '0.8rem',
    lineHeight: 1.35,
    flex: '0 1 auto',
  };
}

function checkboxTitleStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '0.84rem',
    fontWeight: 650,
    lineHeight: 1.4,
  };
}

const complianceGroupStyle = {
  display: 'grid',
  gap: 10,
};

function ownerGroupTitleStyle(ds) {
  return {
    color: ds.colors.textSoft,
    fontSize: '0.84rem',
    fontWeight: 650,
    lineHeight: 1.35,
  };
}

function complianceCheckboxRowStyle(ds) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    color: ds.colors.text,
  };
}

const complianceCheckboxInputStyle = {
  margin: 0,
  flexShrink: 0,
};

function complianceCheckboxTextStyle(ds) {
  return {
    color: ds.colors.text,
    fontSize: '0.84rem',
    fontWeight: 650,
    lineHeight: 1.4,
    minWidth: 0,
  };
}

const invoicePreviewCardStyle = {
  marginTop: 12,
  border: '1px solid #35507f',
  borderRadius: 10,
  background: '#1a2338',
  padding: '0.9rem',
  display: 'grid',
  gap: 8,
};

const invoicePreviewHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 10,
};

const invoicePreviewLogoStyle = {
  maxHeight: 44,
  maxWidth: 160,
  objectFit: 'contain',
  display: 'block',
  marginBottom: 6,
};

const invoicePreviewTitleStyle = {
  color: '#eaf6ff',
  fontWeight: 800,
  fontSize: '1rem',
};

const invoicePreviewMetaStyle = {
  color: '#9fc0e6',
  fontSize: '0.82rem',
  whiteSpace: 'pre-line',
};

const invoicePreviewLineStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#d7ebff',
  fontSize: '0.88rem',
};

const invoicePreviewTotalStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#ffffff',
  fontWeight: 800,
  borderTop: '1px solid #2f466d',
  paddingTop: 8,
};

const invoicePreviewFootnoteStyle = {
  color: '#9fc0e6',
  fontSize: '0.8rem',
};

const invoicePreviewWingitStyle = {
  color: '#8db7e6',
  fontSize: '0.78rem',
  borderTop: '1px solid #2f466d',
  paddingTop: 8,
};

function getMessageTone(message) {
  const value = String(message || '').trim().toLowerCase();

  if (!value) {
    return 'info';
  }

  if (
    value.includes('could not') ||
    value.includes('failed') ||
    value.includes('expired') ||
    value.includes('no stripe account') ||
    value.includes('only the owner')
  ) {
    return 'danger';
  }

  if (
    value.includes('not complete') ||
    value.includes('saved locally') ||
    value.includes('click save') ||
    value.includes('warning')
  ) {
    return 'warning';
  }

  if (
    value.includes('saved') ||
    value.includes('refreshed') ||
    value.includes('linked')
  ) {
    return 'success';
  }

  return 'info';
}

function getStripeStatusMessage(message) {
  const value = String(message || '').trim();
  const normalized = value.toLowerCase();

  if (!value) {
    return '';
  }

  if (
    normalized.includes('stripe') ||
    normalized.includes('session expired') ||
    normalized.includes('owner can link') ||
    normalized.includes('payment feature')
  ) {
    return value;
  }

  return '';
}

function getStripeSummaryStatus({ stripeConnectState, stripeStatusMessage }) {
  const hasError = getMessageTone(stripeStatusMessage) === 'danger';
  const accountId = String(stripeConnectState?.accountId || '').trim();
  const chargesEnabled = stripeConnectState?.chargesEnabled === true;
  const payoutsEnabled = stripeConnectState?.payoutsEnabled === true;

  if (hasError || !accountId) {
    return 'not_connected';
  }

  if (chargesEnabled && payoutsEnabled) {
    return 'connected';
  }

  return 'setup_required';
}

function getStripeSummaryLabel(status) {
  if (status === 'connected') {
    return 'Connected';
  }

  if (status === 'setup_required') {
    return 'Setup required';
  }

  return 'Not connected';
}

function resolveDefaultTaxiTimeMinutes(appSettings) {
  const configuredMinutes = Number(
    appSettings?.techLog?.defaultTaxiTimeMinutes
  );

  if (Number.isFinite(configuredMinutes) && configuredMinutes >= 0) {
    return configuredMinutes;
  }

  return Math.max(
    0,
    Math.round(
      (Number(appSettings?.techLog?.defaultTaxiTimeHours) || 0) * 60
    )
  );
}
